import React, { useState, useEffect, useRef } from 'react';
import {
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  Cloud,
  ArrowRight,
  ArrowLeft,
  Mail,
  CheckCircle2,
  Check,
  Lock,
  User,
  ShieldCheck
} from 'lucide-react';
import api from '../services/api';
import {
  signupSchema,
  loginSchema,
  validateEmailField,
  validatePasswordField,
  validateConfirmPasswordField,
  getPasswordCriteria,
  getPasswordStrength
} from '../utils/authValidation';
import '../styles/login.css';

function Login({ onLogin, initialIsSignUp = false, onBack, initialError = '' }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(initialIsSignUp);

  // Field inline errors & touched state
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false
  });

  const [error, setError] = useState(initialError || '');

  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);
  const [loading, setLoading] = useState(false);

  // OTP Screen State
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [isVerifiedSuccess, setIsVerifiedSuccess] = useState(false);

  const inputRefs = useRef([]);

  // Forgot Password Flow States (kept strictly in React state)
  const [forgotStep, setForgotStep] = useState(null); // 'email' | 'code' | 'newPassword' | null
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotDigits, setForgotDigits] = useState(['', '', '', '', '', '']);
  const [forgotResetToken, setForgotResetToken] = useState('');
  const [forgotCooldown, setForgotCooldown] = useState(0);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmNewPwd, setConfirmNewPwd] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmNewPwd, setShowConfirmNewPwd] = useState(false);
  const [successNotice, setSuccessNotice] = useState('');

  const forgotInputRefs = useRef([]);

  // Sync state if initial prop changes
  useEffect(() => {
    setIsSignUp(initialIsSignUp);
    clearFormState();
  }, [initialIsSignUp]);

  const clearFormState = () => {
    setError('');
    setFullName('');
    setFieldErrors({ email: '', password: '', confirmPassword: '' });
    setTouched({ email: false, password: false, confirmPassword: false });
  };

  const handleTabSwitch = (signUpMode) => {
    if (loading) return;
    setIsSignUp(signUpMode);
    clearFormState();
    setPassword('');
    setConfirmPassword('');
    if (email.trim().length > 0) {
      const err = validateEmailField(email, signUpMode);
      setFieldErrors((prev) => ({ ...prev, email: err }));
    }
  };

  // Resend cooldown countdown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Focus the first OTP input box when the OTP screen opens
  useEffect(() => {
    if (showOtpScreen && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 50);
    }
  }, [showOtpScreen]);

  // Forgot password cooldown countdown timer
  useEffect(() => {
    if (forgotCooldown <= 0) return;
    const timer = setInterval(() => {
      setForgotCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [forgotCooldown]);

  // Focus the first OTP box when forgot password code screen opens
  useEffect(() => {
    if (forgotStep === 'code' && forgotInputRefs.current[0]) {
      setTimeout(() => {
        forgotInputRefs.current[0]?.focus();
      }, 50);
    }
  }, [forgotStep]);

  // OAuth popup listener
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      // Security: Validate message origin
      const allowedOrigins = [
        window.location.origin,
        process.env.REACT_APP_API_BASE_URL ? new URL(process.env.REACT_APP_API_BASE_URL, window.location.origin).origin : null
      ].filter(Boolean);
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (event.data && event.data.type === 'git-oauth-auth-result') {
        const { success, data, error: oauthError, provider } = event.data;
        if (success && data) {
          api.setSession(data);
          onLogin(data, false);
        } else {
          setError(oauthError || `Failed to log in with ${provider}`);
        }
        setLoading(false);
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => {
      window.removeEventListener('message', handleOAuthMessage);
    };
  }, [onLogin]);

  const handleConnectOAuth = async (provider) => {
    setError('');
    setFullName('');
    setLoading(true);
    try {
      const data = await api.getGitOAuthAuthUrl(provider);
      const popup = window.open(data.authUrl, 'git-oauth-popup', 'width=680,height=760');
      if (!popup) {
        setError('Popup blocked. Please allow popups and try again.');
        setLoading(false);
        return;
      }
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          setLoading(false);
        }
      }, 1000);
    } catch (err) {
      setError(`Failed to start OAuth for ${provider}: ${err.message}`);
      setLoading(false);
    }
  };

  // ── Live Inline Validation Handlers ──
  const handleEmailChange = (val) => {
    setEmail(val);
    if (val.trim().length > 0) {
      const err = validateEmailField(val, isSignUp);
      setFieldErrors((prev) => ({ ...prev, email: err }));
    } else {
      setFieldErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  const handleEmailBlur = () => {
    setTouched((prev) => ({ ...prev, email: true }));
    if (true) {
      const err = validateEmailField(email, isSignUp);
      setFieldErrors((prev) => ({ ...prev, email: err }));
    }
  };

  const handlePasswordChange = (val) => {
    setPassword(val);
    if (!isSignUp && val) setFieldErrors((prev) => ({ ...prev, password: '' }));
    if (isSignUp) {
      if (val.length > 0) {
        const err = validatePasswordField(val, true);
        setFieldErrors((prev) => ({ ...prev, password: err }));
      } else {
        setFieldErrors((prev) => ({ ...prev, password: '' }));
      }
      if (confirmPassword.length > 0) {
        const cErr = validateConfirmPasswordField(val, confirmPassword);
        setFieldErrors((prev) => ({ ...prev, confirmPassword: cErr }));
      }
    }
  };

  const handlePasswordBlur = () => {
    setTouched((prev) => ({ ...prev, password: true }));
    if (true) {
      const err = validatePasswordField(password, isSignUp);
      setFieldErrors((prev) => ({ ...prev, password: err }));
    }
  };

  const handleConfirmPasswordChange = (val) => {
    setConfirmPassword(val);
    if (val.length > 0) {
      const err = validateConfirmPasswordField(password, val);
      setFieldErrors((prev) => ({ ...prev, confirmPassword: err }));
    } else {
      setFieldErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleConfirmPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    if (confirmPassword.length > 0 || isSignUp) {
      const err = validateConfirmPasswordField(password, confirmPassword);
      setFieldErrors((prev) => ({ ...prev, confirmPassword: err }));
    }
  };

  // ── Submit OTP Verification with Smooth Redirect Transition ──
  const handleVerifyOtp = async (codeToVerify) => {
    if (isVerifiedSuccess) return;
    const code = codeToVerify || otpDigits.join('');
    if (code.length !== 6) {
      setOtpError('Please enter all 6 digits of your verification code.');
      return;
    }
    setOtpError('');
    setOtpLoading(true);
    try {
      const res = await api.verifyOtp(otpEmail, code);
      api.setSession(res);

      // Trigger Smooth Success State
      setIsVerifiedSuccess(true);
      setOtpLoading(false);

      // 1.3 seconds gentle pause for pleasant transition
      setTimeout(() => {
        onLogin(res, true);
      }, 1300);
    } catch (err) {
      setOtpError(err.message || 'Verification failed. Please try again.');
      setOtpLoading(false);
    }
  };

  // Resend OTP Code
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || resendLoading || isVerifiedSuccess) return;
    setOtpError('');
    setResendLoading(true);
    try {
      const res = await api.resendOtp(otpEmail);
      const wait = res.retryAfterSeconds || 60;
      setResendCooldown(wait);
    } catch (err) {
      if (err.status === 429 && err.retryAfterSeconds) {
        setResendCooldown(err.retryAfterSeconds);
      }
      setOtpError(err.message || 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  // ── Forgot Password Handlers ──
  const handleStartForgotPassword = () => {
    setError('');
    setFullName('');
    setSuccessNotice('');
    setForgotStep('email');
    setForgotEmail(email.trim().toLowerCase());
    setForgotDigits(['', '', '', '', '', '']);
    setForgotError('');
    setForgotResetToken('');
  };

  const handleSendForgotCode = async (e) => {
    if (e) e.preventDefault();
    if (!forgotEmail || !forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotError('');
    setForgotLoading(true);
    try {
      const res = await api.forgotPassword(forgotEmail.trim().toLowerCase());
      setForgotCooldown(res.retryAfterSeconds || 60);
      setForgotStep('code');
    } catch (err) {
      setForgotError(err.message || 'Failed to send reset code.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendForgotCode = async () => {
    if (forgotCooldown > 0 || forgotLoading) return;
    setForgotError('');
    setForgotLoading(true);
    try {
      const res = await api.forgotPassword(forgotEmail.trim().toLowerCase());
      setForgotCooldown(res.retryAfterSeconds || 60);
    } catch (err) {
      setForgotError(err.message || 'Failed to resend reset code.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyForgotCode = async (codeOverride) => {
    const code = codeOverride || forgotDigits.join('');
    if (code.length !== 6) {
      setForgotError('Please enter all 6 digits of your reset code.');
      return;
    }
    setForgotError('');
    setForgotLoading(true);
    try {
      const res = await api.verifyResetCode(forgotEmail.trim().toLowerCase(), code);
      setForgotResetToken(res.resetToken);
      setForgotStep('newPassword');
    } catch (err) {
      setForgotError(err.message || 'Invalid or expired code.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...forgotDigits];
    newDigits[index] = digit;
    setForgotDigits(newDigits);

    if (digit && index < 5) {
      forgotInputRefs.current[index + 1]?.focus();
    }

    if (newDigits.every((d) => d !== '')) {
      handleVerifyForgotCode(newDigits.join(''));
    }
  };

  const handleForgotOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!forgotDigits[index] && index > 0) {
        forgotInputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      forgotInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      forgotInputRefs.current[index + 1]?.focus();
    }
  };

  const handleForgotOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const newDigits = [...forgotDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || '';
    }
    setForgotDigits(newDigits);

    const nextIndex = Math.min(pasted.length, 5);
    forgotInputRefs.current[nextIndex]?.focus();

    if (pasted.length === 6) {
      handleVerifyForgotCode(pasted);
    }
  };

  const handleResetNewPassword = async (e) => {
    if (e) e.preventDefault();
    if (newPwd.length < 8) {
      setForgotError('Password must be at least 8 characters long.');
      return;
    }
    if (newPwd !== confirmNewPwd) {
      setForgotError('Passwords do not match.');
      return;
    }
    setForgotError('');
    setForgotLoading(true);
    try {
      await api.resetPassword(forgotResetToken, newPwd);
      setForgotStep(null);
      setForgotResetToken('');
      setNewPwd('');
      setConfirmNewPwd('');
      setForgotDigits(['', '', '', '', '', '']);
      setIsSignUp(false);
      setSuccessNotice('Your password was updated. Please sign in.');
    } catch (err) {
      setForgotError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // OTP Input Handlers
  const handleOtpChange = (index, value) => {
    if (isVerifiedSuccess) return;
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newDigits.every((d) => d !== '')) {
      handleVerifyOtp(newDigits.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (isVerifiedSuccess) return;
    if (e.key === 'Backspace') {
      if (!otpDigits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    if (isVerifiedSuccess) return;
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const newDigits = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || '';
    }
    setOtpDigits(newDigits);

    const nextIndex = Math.min(pasted.length, 5);
    inputRefs.current[nextIndex]?.focus();

    if (pasted.length === 6) {
      handleVerifyOtp(pasted);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFullName('');

    // Mark all touched
    setTouched({ email: true, password: true, confirmPassword: true });

    // Validate using Zod schemas
    if (isSignUp) {
      const validation = signupSchema.safeParse({ email, password, confirmPassword });
      if (!validation.success) {
        const errors = {};
        const issues = validation.error?.issues || validation.error?.errors || [];
        issues.forEach((err) => {
          const field = err.path?.[0];
          if (field && !errors[field]) errors[field] = err.message;
        });
        setFieldErrors(errors);
        return;
      }
    } else {
      const validation = loginSchema.safeParse({ email, password });
      if (!validation.success) {
        const errors = {};
        const issues = validation.error?.issues || validation.error?.errors || [];
        issues.forEach((err) => {
          const field = err.path?.[0];
          if (field && !errors[field]) errors[field] = err.message;
        });
        setFieldErrors(errors);
        return;
      }
    }

    setFieldErrors({ email: '', password: '', confirmPassword: '' });
    setLoading(true);

    try {
      if (isSignUp) {
        const plan = localStorage.getItem('selectedPlan') || 'hobby';
        const res = await api.signup(email.trim().toLowerCase(), password, plan, fullName ? fullName.trim() : null);
        if (res && res.requiresVerification) {
          setOtpEmail(res.email || email.trim().toLowerCase());
          setOtpDigits(['', '', '', '', '', '']);
          setOtpError('');
          setIsVerifiedSuccess(false);
          setResendCooldown(res.retryAfterSeconds || 60);
          setShowOtpScreen(true);
          return;
        }
        localStorage.setItem('lastEmail', email.trim().toLowerCase());
        onLogin(res, true);
      } else {
        const user = await api.login(email.trim().includes('@') ? email.trim().toLowerCase() : email.trim(), password);
        localStorage.setItem('lastEmail', email.trim().includes('@') ? email.trim().toLowerCase() : email.trim());
        onLogin(user, false);
      }
    } catch (err) {
      if (err.requiresVerification) {
        if (err.canSendOtp === false || !err.email) {
          setError(err.message || 'Account email is not verified and cannot receive OTP codes. Please contact your administrator.');
          return;
        }

        const targetEmail = err.email;
        setOtpEmail(targetEmail);
        setOtpDigits(['', '', '', '', '', '']);
        setOtpError('');
        setIsVerifiedSuccess(false);
        setShowOtpScreen(true);

        try {
          const resendRes = await api.resendOtp(targetEmail);
          setResendCooldown(resendRes.retryAfterSeconds || 60);
        } catch (resendErr) {
          if (resendErr.status === 429 && resendErr.retryAfterSeconds) {
            setResendCooldown(resendErr.retryAfterSeconds);
          } else {
            setOtpError(resendErr.message || 'Could not automatically send verification code. Please click "Resend code".');
          }
        }
        return;
      }
      setError(err.message || (isSignUp ? 'Sign up failed' : 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  const passwordCriteria = getPasswordCriteria(password);
  const passwordStrength = getPasswordStrength(password);
  const isPasswordsMatch = isSignUp && confirmPassword.length > 0 && password === confirmPassword;

  // Real-time validation flags
  const isEmailError = touched.email && !!fieldErrors.email;
  const isEmailValid = isSignUp && email.trim().length > 0 && !fieldErrors.email;

  const isPasswordError = touched.password && !!fieldErrors.password;
  const isPasswordValid = isSignUp && password.length > 0 && !fieldErrors.password;

  const isConfirmError = isSignUp && touched.confirmPassword && (!!fieldErrors.confirmPassword || (confirmPassword.length > 0 && password !== confirmPassword));
  const isConfirmValid = isSignUp && confirmPassword.length > 0 && isPasswordsMatch;

  // ── Render OTP Verification Screen ──
  if (showOtpScreen) {
    return (
      <div className="lp-root">
        <div className="lp-glass-card lp-otp-card" style={{ textAlign: 'center' }}>
          {isVerifiedSuccess ? (
            <div className="lp-success-state fade-in">
              <div className="lp-success-icon-wrap">
                <CheckCircle2 size={56} className="lp-success-icon" />
              </div>
              <h2 className="lp-success-title">Email Verified!</h2>
              <p className="lp-success-subtitle">
                Welcome to Devorants! Preparing your workspace...
              </p>
              <div className="lp-success-progress-bar">
                <div className="lp-success-progress-fill" />
              </div>
            </div>
          ) : (
            <>
              <div className="lp-logo-container">
                <div className="lp-logo">
                  <Mail size={24} strokeWidth={2.5} />
                </div>
              </div>

              <h2 style={{ marginBottom: '0.5rem', color: 'var(--text-main)', fontSize: '1.5rem', fontWeight: 700 }}>
                Enter Verification Code
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                We've sent a 6-digit verification code to<br />
                <strong style={{ color: 'var(--text-main)', wordBreak: 'break-all' }}>{otpEmail}</strong>
              </p>

              {otpError && (
                <div className="lp-error" role="alert" style={{ marginBottom: '1.25rem' }}>
                  <AlertCircle size={18} />
                  <span>{otpError}</span>
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }}>
                <div className="lp-otp-container" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      className={`lp-otp-input ${digit ? 'has-value' : ''}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      disabled={otpLoading}
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  className="lp-submit"
                  disabled={otpLoading || otpDigits.some((d) => d === '')}
                  style={{ marginTop: '1rem' }}
                >
                  {otpLoading ? (
                    <>
                      <Loader2 size={18} className="lp-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify Email</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <div className="lp-otp-resend">
                <span>Didn't receive the code?</span>
                <button
                  type="button"
                  className="lp-otp-resend-btn"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || resendLoading}
                >
                  {resendLoading ? (
                    <Loader2 size={14} className="lp-spin" style={{ display: 'inline', marginRight: 4 }} />
                  ) : null}
                  {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
                </button>
              </div>

              <div style={{ marginTop: '1.25rem' }}>
                <button
                  type="button"
                  className="lp-otp-back-btn"
                  onClick={() => {
                    setShowOtpScreen(false);
                    setOtpDigits(['', '', '', '', '', '']);
                    setOtpError('');
                  }}
                >
                  <ArrowLeft size={16} />
                  <span>Wrong email? Go back</span>
                </button>
              </div>
            </>
          )}
        </div>

        <div className="lp-copy">&copy; {new Date().getFullYear()} Devorants</div>
      </div>
    );
  }

// ── Render Forgot Password Flow ──
  if (forgotStep) {
    return (
      <div className="lp-root">
        <div className="lp-glass-card lp-otp-card" style={{ textAlign: 'center' }}>
          {/* Step 1: Email Screen */}
          {forgotStep === 'email' && (
            <>
              <div className="lp-logo-container">
                <div className="lp-logo">
                  <Lock size={24} strokeWidth={2.5} />
                </div>
              </div>

              <h2 style={{ marginBottom: '0.5rem', color: 'var(--text-main)', fontSize: '1.5rem', fontWeight: 700 }}>
                Reset Your Password
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.25rem', lineHeight: '1.5' }}>
                Enter your account email address.<br />
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  If an account exists for that email, we will send a code.
                </span>
              </p>

              {forgotError && (
                <div className="lp-error" role="alert" style={{ marginBottom: '1.25rem' }}>
                  <AlertCircle size={18} />
                  <span>{forgotError}</span>
                </div>
              )}

              <form onSubmit={handleSendForgotCode} noValidate>
                <div className="lp-field" style={{ textAlign: 'left', marginBottom: '1.25rem' }}>
                  <label htmlFor="lp-forgot-email" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-main)' }}>
                    Email Address
                  </label>
                  <div className="lp-input-wrap">
                    <input
                      id="lp-forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => {
                        setForgotEmail(e.target.value);
                        setForgotError('');
                      }}
                      disabled={forgotLoading}
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="lp-submit"
                  disabled={forgotLoading || !forgotEmail.trim()}
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 size={18} className="lp-spin" />
                      <span>Sending code...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Reset Code</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <div style={{ marginTop: '1.25rem' }}>
                <button
                  type="button"
                  className="lp-otp-back-btn"
                  onClick={() => {
                    setForgotStep(null);
                    setForgotError('');
                  }}
                >
                  <ArrowLeft size={16} />
                  <span>Back to Sign In</span>
                </button>
              </div>
            </>
          )}

          {/* Step 2: Code Screen */}
          {forgotStep === 'code' && (
            <>
              <div className="lp-logo-container">
                <div className="lp-logo">
                  <ShieldCheck size={24} strokeWidth={2.5} />
                </div>
              </div>

              <h2 style={{ marginBottom: '0.5rem', color: 'var(--text-main)', fontSize: '1.5rem', fontWeight: 700 }}>
                Enter Reset Code
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                We've sent a 6-digit password reset code to<br />
                <strong style={{ color: 'var(--text-main)', wordBreak: 'break-all' }}>{forgotEmail}</strong>
              </p>

              {forgotError && (
                <div className="lp-error" role="alert" style={{ marginBottom: '1.25rem' }}>
                  <AlertCircle size={18} />
                  <span>{forgotError}</span>
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleVerifyForgotCode(); }}>
                <div className="lp-otp-container" onPaste={handleForgotOtpPaste}>
                  {forgotDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (forgotInputRefs.current[idx] = el)}
                      className={`lp-otp-input ${digit ? 'has-value' : ''}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                      value={digit}
                      onChange={(e) => handleForgotOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleForgotOtpKeyDown(idx, e)}
                      disabled={forgotLoading}
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  className="lp-submit"
                  disabled={forgotLoading || forgotDigits.some((d) => d === '')}
                  style={{ marginTop: '1rem' }}
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 size={18} className="lp-spin" />
                      <span>Verifying code...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify Code</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <div className="lp-otp-resend">
                <span>Didn't receive the code?</span>
                <button
                  type="button"
                  className="lp-otp-resend-btn"
                  onClick={handleResendForgotCode}
                  disabled={forgotCooldown > 0 || forgotLoading}
                >
                  {forgotLoading ? (
                    <Loader2 size={14} className="lp-spin" style={{ display: 'inline', marginRight: 4 }} />
                  ) : null}
                  {forgotCooldown > 0 ? `Resend code (${forgotCooldown}s)` : 'Resend code'}
                </button>
              </div>

              <div style={{ marginTop: '1.25rem' }}>
                <button
                  type="button"
                  className="lp-otp-back-btn"
                  onClick={() => {
                    setForgotStep('email');
                    setForgotDigits(['', '', '', '', '', '']);
                    setForgotError('');
                  }}
                >
                  <ArrowLeft size={16} />
                  <span>Wrong email? Go back</span>
                </button>
              </div>
            </>
          )}

          {/* Step 3: New Password Screen */}
          {forgotStep === 'newPassword' && (
            <>
              <div className="lp-logo-container">
                <div className="lp-logo">
                  <Lock size={24} strokeWidth={2.5} />
                </div>
              </div>

              <h2 style={{ marginBottom: '0.5rem', color: 'var(--text-main)', fontSize: '1.5rem', fontWeight: 700 }}>
                Set New Password
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                Create a new password for <strong style={{ color: 'var(--text-main)' }}>{forgotEmail}</strong>.
              </p>

              {forgotError && (
                <div className="lp-error" role="alert" style={{ marginBottom: '1.25rem' }}>
                  <AlertCircle size={18} />
                  <span>{forgotError}</span>
                </div>
              )}

              <form onSubmit={handleResetNewPassword} style={{ textAlign: 'left' }} noValidate>
                {/* New Password */}
                <div className="lp-field" style={{ marginBottom: '1.25rem' }}>
                  <label htmlFor="lp-new-pwd" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-main)' }}>
                    New Password
                  </label>
                  <div className="lp-pw-wrap">
                    <input
                      id="lp-new-pwd"
                      type={showNewPwd ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      value={newPwd}
                      onChange={(e) => {
                        setNewPwd(e.target.value);
                        setForgotError('');
                      }}
                      disabled={forgotLoading}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="lp-pw-toggle"
                      onClick={() => setShowNewPwd((v) => !v)}
                      aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
                    Must be at least 8 characters long
                  </span>
                </div>

                {/* Confirm New Password */}
                <div className="lp-field" style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="lp-confirm-new-pwd" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-main)' }}>
                    Confirm New Password
                  </label>
                  <div className="lp-pw-wrap">
                    <input
                      id="lp-confirm-new-pwd"
                      type={showConfirmNewPwd ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      value={confirmNewPwd}
                      onChange={(e) => {
                        setConfirmNewPwd(e.target.value);
                        setForgotError('');
                      }}
                      disabled={forgotLoading}
                    />
                    <button
                      type="button"
                      className="lp-pw-toggle"
                      onClick={() => setShowConfirmNewPwd((v) => !v)}
                      aria-label={showConfirmNewPwd ? 'Hide confirm password' : 'Show confirm password'}
                      tabIndex={-1}
                    >
                      {showConfirmNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="lp-submit"
                  disabled={forgotLoading || !newPwd || !confirmNewPwd}
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 size={18} className="lp-spin" />
                      <span>Updating password...</span>
                    </>
                  ) : (
                    <>
                      <span>Reset Password</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <div style={{ marginTop: '1.25rem' }}>
                <button
                  type="button"
                  className="lp-otp-back-btn"
                  onClick={() => {
                    setForgotStep(null);
                    setForgotResetToken('');
                    setNewPwd('');
                    setConfirmNewPwd('');
                    setForgotError('');
                  }}
                >
                  <ArrowLeft size={16} />
                  <span>Cancel and return to Sign In</span>
                </button>
              </div>
            </>
          )}
        </div>

        <div className="lp-copy">&copy; {new Date().getFullYear()} Devorants</div>
      </div>
    );
  }

  // ── Render Standard Login / Signup Screen ──
  return (
    <div className="lp-root">
      {onBack && (
        <button
          type="button"
          className="lp-back-link"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          <span>Back to Home</span>
        </button>
      )}
      <div className="lp-glass-card">
        {/* Brand Header */}
        <div className="lp-logo-container">
          <div className="lp-logo">
            <Cloud size={28} strokeWidth={2.5} />
          </div>
        </div>

        {/* Segmented Mode Switcher */}
        <div className="lp-tabs-container">
          <button
            type="button"
            className={`lp-tab ${!isSignUp ? 'active' : ''}`}
            onClick={() => handleTabSwitch(false)}
          >
            Sign In
          </button>
          <button
            type="button"
            className={`lp-tab ${isSignUp ? 'active' : ''}`}
            onClick={() => handleTabSwitch(true)}
          >
            Create Account
          </button>
        </div>

        <div className="lp-form-header">
          <h2>{isSignUp ? 'Get Started Free' : 'Welcome Back'}</h2>
          <p>{isSignUp ? 'Create your account to start deploying in seconds' : 'Sign in to manage and scale your deployments'}</p>
        </div>

        {successNotice && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid #10b981',
            color: '#065f46',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}>
            <CheckCircle2 size={18} color="#10b981" />
            <span>{successNotice}</span>
          </div>
        )}

        {error && (
          <div className="lp-error" role="alert" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
              <AlertCircle size={18} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
            {(error.toLowerCase().includes('support') || error.toLowerCase().includes('deactivated') || error.toLowerCase().includes('suspended')) && (
              <a
                href="mailto:support@devorants.com?subject=Account%20Suspension%20or%20Access%20Inquiry"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: 'inherit',
                  textDecoration: 'none',
                  paddingLeft: '1.6rem',
                  marginTop: '0.2rem'
                }}
              >
                <Mail size={14} style={{ flexShrink: 0 }} />
                <span style={{ textDecoration: 'underline' }}>Contact Support</span>
              </a>
            )}
          </div>
        )}

        {/* OAuth Buttons (Side-by-Side 2 Columns) */}
        <div className="lp-oauth-group">
          <button type="button" onClick={() => handleConnectOAuth('github')} className="lp-oauth-btn" disabled={loading}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
            </svg>
            <span>GitHub</span>
          </button>
          <button type="button" onClick={() => handleConnectOAuth('gitlab')} className="lp-oauth-btn" disabled={loading}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="#e24329">
              <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 5.14 2h.02a.42.42 0 0 1 .4.28l2.44 7.51h8l2.44-7.51A.42.42 0 0 1 18.84 2h.02a.42.42 0 0 1 .4.28l2.44 7.51 1.22 3.78a.84.84 0 0 1-.27.94z"/>
            </svg>
            <span>GitLab</span>
          </button>
        </div>

        <div className="lp-divider">
          <span>OR CONTINUE WITH EMAIL</span>
        </div>

        <form onSubmit={handleSubmit} className="lp-form" noValidate>
          {/* Full Name (Sign Up only) */}
          {isSignUp && (
            <div className="lp-field">
              <div className="lp-field-header">
                <label htmlFor="lp-fullname">Full Name</label>
              </div>
              <div className="lp-input-wrap">
                <input
                  id="lp-fullname"
                  type="text"
                  placeholder="e.g. Sarah Ahmed"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          )}
          {/* Email Address */}
          <div className="lp-field">
            <div className="lp-field-header">
              <label htmlFor="lp-email">{isSignUp ? 'Email Address' : 'Username or Email'}</label>
              {isSignUp && isEmailValid && (
                <span className="lp-valid-badge">
                  <Check size={11} /> Valid
                </span>
              )}
            </div>
            <div className="lp-input-wrap">
              <input
                id="lp-email"
                type="email"
                placeholder={isSignUp ? 'you@example.com' : 'username or name@example.com'}
                autoComplete="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                disabled={loading}
                className={isEmailError ? 'lp-input-error' : (isEmailValid ? 'lp-input-valid' : '')}
              />
            </div>
            {isEmailError && (
              <div className="lp-inline-error">
                <AlertCircle size={13} />
                <span>{fieldErrors.email}</span>
              </div>
            )}
          </div>

          {/* Password */}
          <div className="lp-field">
            <div className="lp-field-header">
              <label htmlFor="lp-password">Password</label>
              {isSignUp && password && (
                <span className="lp-strength-badge" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              )}
            </div>
            <div className="lp-pw-wrap">
              <input
                id="lp-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onBlur={handlePasswordBlur}
                disabled={loading}
                className={isPasswordError ? 'lp-input-error' : (isPasswordValid ? 'lp-input-valid' : '')}
              />
              <button
                type="button"
                className="lp-pw-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {/* Compact Slim Password Strength Bar & Checklist */}
            {isSignUp && password && (
              <div className="lp-pw-compact-box">
                <div className="lp-strength-track">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className="lp-strength-bar"
                      style={{
                        background: step <= passwordStrength.score ? passwordStrength.color : 'var(--border)'
                      }}
                    />
                  ))}
                </div>
                <div className="lp-criteria-grid">
                  <div className={`lp-criteria-chip ${passwordCriteria.minLength ? 'met' : ''}`}>
                    <Check size={10} className="lp-chip-icon" />
                    <span>8+ Chars</span>
                  </div>
                  <div className={`lp-criteria-chip ${passwordCriteria.hasUpper ? 'met' : ''}`}>
                    <Check size={10} className="lp-chip-icon" />
                    <span>Uppercase</span>
                  </div>
                  <div className={`lp-criteria-chip ${passwordCriteria.hasLower ? 'met' : ''}`}>
                    <Check size={10} className="lp-chip-icon" />
                    <span>Lowercase</span>
                  </div>
                  <div className={`lp-criteria-chip ${passwordCriteria.hasNumber ? 'met' : ''}`}>
                    <Check size={10} className="lp-chip-icon" />
                    <span>Number</span>
                  </div>
                </div>
              </div>
            )}
            {isPasswordError && (
              <div className="lp-inline-error">
                <AlertCircle size={13} />
                <span>{fieldErrors.password}</span>
              </div>
            )}
          </div>

          {!isSignUp && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.35rem', marginBottom: '0.85rem' }}>
              <button
                type="button"
                onClick={handleStartForgotPassword}
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  padding: '2px 4px',
                  color: '#4f46e5',
                  fontSize: '0.825rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Confirm Password (Signup Mode only) */}
          {isSignUp && (
            <div className="lp-field fade-in">
              <div className="lp-field-header">
                <label htmlFor="lp-confirm-password">Confirm Password</label>
                {isConfirmValid && (
                  <span className="lp-valid-badge">
                    <Check size={11} /> Passwords match
                  </span>
                )}
              </div>
              <div className="lp-pw-wrap">
                <input
                  id="lp-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                  onBlur={handleConfirmPasswordBlur}
                  disabled={loading}
                  className={isConfirmError ? 'lp-input-error' : (isConfirmValid ? 'lp-input-valid' : '')}
                />
                <button
                  type="button"
                  className="lp-pw-toggle"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {isConfirmError && (
                <div className="lp-inline-error">
                  <AlertCircle size={13} />
                  <span>{fieldErrors.confirmPassword || 'Passwords do not match'}</span>
                </div>
              )}
            </div>
          )}

          <button type="submit" className="lp-submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="lp-spin" />
                <span>{isSignUp ? 'Creating account...' : 'Signing in...'}</span>
              </>
            ) : (
              <>
                <span>{isSignUp ? 'Create Free Account' : 'Sign In with Email'}</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <div className="lp-footer-switch">
          {isSignUp ? (
            <span>
              Already have an account?{' '}
              <button
                type="button"
                className="lp-link-btn"
                onClick={() => handleTabSwitch(false)}
              >
                Sign in
              </button>
            </span>
          ) : (
            <span>
              Don't have an account?{' '}
              <button
                type="button"
                className="lp-link-btn"
                onClick={() => handleTabSwitch(true)}
              >
                Sign up for free
              </button>
            </span>
          )}
        </div>
      </div>

      <div className="lp-copy">&copy; {new Date().getFullYear()} Devorants · Cloud Deployments</div>
    </div>
  );
}

export default Login;
